import { Error } from "@/components/admin/error";
import { Notification } from "@/components/admin/notification";
import { Skeleton } from "@/components/ui/skeleton";
import { Suspense, type ReactNode } from "react";
import { ErrorBoundary } from "react-error-boundary";

import { useConfigurationLoader } from "../root/useConfigurationLoader";
import { useDynamicBranding } from "../root/useDynamicBranding";
import { useCRM } from "../context/CRMContext";
import { useLogout } from "ra-core";
import { Button } from "@/components/ui/button";
import { ShieldAlert, LogOut } from "lucide-react";
import { MobileNavigation } from "./MobileNavigation";
import { GlobalSearchProvider } from "../search/GlobalSearchContext";
import { GlobalSearchDialog } from "../search/GlobalSearchDialog";

export const MobileLayout = ({ children }: { children: ReactNode }) => {
  useConfigurationLoader();
  useDynamicBranding();
  const { currentCrm, isLoading, isSuperadmin, refreshCrms } = useCRM();
  const logout = useLogout();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-xs text-muted-foreground animate-pulse">
            Carregando ambiente...
          </p>
        </div>
      </div>
    );
  }

  if (!isLoading && !currentCrm && !isSuperadmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-md w-full bg-card border border-border rounded-xl p-8 shadow-lg text-center space-y-4">
          <div className="w-14 h-14 bg-destructive/10 text-destructive rounded-full flex items-center justify-center mx-auto">
            <ShieldAlert className="w-8 h-8" />
          </div>
          <h2 className="text-xl font-bold tracking-tight text-foreground">
            Acesso Indisponível
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Seu CRM foi desativado ou excluído pelo administrador da plataforma.
            Você não possui acesso a nenhum ambiente no momento.
          </p>
          <div className="pt-2 flex flex-col gap-2">
            <Button
              variant="default"
              onClick={() => refreshCrms()}
              className="w-full text-xs cursor-pointer"
            >
              Tentar novamente
            </Button>
            <Button
              variant="outline"
              onClick={() => logout()}
              className="gap-2 w-full text-xs cursor-pointer"
            >
              <LogOut className="w-4 h-4" />
              Sair da Conta
            </Button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <GlobalSearchProvider>
      <ErrorBoundary FallbackComponent={Error}>
        <Suspense fallback={<Skeleton className="h-12 w-12 rounded-full" />}>
          {children}
        </Suspense>
      </ErrorBoundary>
      <MobileNavigation />
      <Notification mobileOffset={{ bottom: "72px" }} />
      <GlobalSearchDialog />
    </GlobalSearchProvider>
  );
};
